import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useOrganization } from '../context/OrganizationContext';
import { useUserOrganizations, useUserTeams } from '../hooks/queries';

import { userService, organizationService, teamService, subscriptionService, paymentService, invitationService, roleService, contentService, tagService, searchService, authService } from '../services';
import InvitationList from '../components/InvitationList';
import { StatCard } from '../components/common/StatCard';
import { EmptyState } from '../components/common/EmptyState';
import { ledeSx } from '../theme/styleHelpers';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Box,
  Alert,
  AlertTitle,
  Divider,
  Chip,
  Stack,
  CircularProgress,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Badge,
  Skeleton,
  CardHeader,
  Tooltip,
} from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import {
  Security,
  Add as AddIcon,
  ArrowForward,
  Business,
  Group,
  Refresh,
  NotificationsActive,
  Settings,
  Star,
} from '@mui/icons-material';
import { ViewsDashboardCard } from '../components/views';
import SubscriptionExpirationWarnings from '../components/subscription/SubscriptionExpirationWarnings';

interface DashboardData {
  summary: {
    organizationsCount: number;
    teamsCount: number;
    pendingInvitationsCount: number;
    membershipsCount: number;
  };
}

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const {
    invitations,
    syncWithCurrentRoute,
    setCurrentTeamId,
    setCurrentOrganizationId,
    getOrganizationSubscriptions,
    currentTeamId,
    teams
  } = useOrganization();

  // Use React Query for data fetching (automatic deduplication!)
  const {
    data: userOrganizations = [],
    isLoading: orgsLoading,
    isError: orgsError,
    refetch: refetchOrgs,
  } = useUserOrganizations();
  const {
    data: userTeams = [],
    isLoading: teamsLoading,
    isError: teamsError,
    refetch: refetchTeams,
  } = useUserTeams();

  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [membershipsError, setMembershipsError] = useState(false);
  const [resendingVerification, setResendingVerification] = useState(false);
  const [verificationMessage, setVerificationMessage] = useState<string | null>(null);
  const [activatingTeamId, setActivatingTeamId] = useState<string | null>(null);
  const hasRefreshedInvitations = useRef(false);

  // Count pending invitations
  const pendingInvitationsCount = invitations.filter(inv => inv.status === 'pending').length;

  // Collect all subscriptions from all user organizations
  const allUserSubscriptions = React.useMemo(() => {
    const subs: any[] = [];
    userOrganizations.forEach(org => {
      const orgSubs = getOrganizationSubscriptions(org.id || org._id);
      subs.push(...orgSubs);
    });
    return subs;
  }, [userOrganizations, getOrganizationSubscriptions]);

  const handleResendVerification = async () => {
    if (!user?.email) return;

    setResendingVerification(true);
    setVerificationMessage(null);

    try {
      const result = await authService.sendVerificationEmail(user.email);
      setVerificationMessage(result.message || 'Verification email sent successfully');

      // Clear the message after 5 seconds
      setTimeout(() => {
        setVerificationMessage(null);
      }, 5000);
    } catch (error) {
      console.error('Failed to resend verification email:', error);
      setVerificationMessage('Failed to resend verification email. Please try again.');

      // Clear the error message after 5 seconds
      setTimeout(() => {
        setVerificationMessage(null);
      }, 5000);
    } finally {
      setResendingVerification(false);
    }
  };

  const loadDashboardData = async () => {
    if (!user) return;

    setLoading(true);
    setError(null);
    setMembershipsError(false);

    try {
      // React Query is already fetching orgs and teams (no duplicate calls!)
      // Only fetch memberships and invitations here
      const results = await Promise.allSettled([
        userService.getUserMemberships(),
        userService.getUserInvitations()
      ]);

      // Extract successful results, use empty arrays/objects for failed ones
      const userMembershipsResult = results[0].status === 'fulfilled' ? results[0].value : { memberships: [] };
      const userInvitations = results[1].status === 'fulfilled' ? results[1].value : [];

      // Log any failed promises
      results.forEach((result, index) => {
        if (result.status === 'rejected') {
          console.error(`🚨 Dashboard API call ${index} failed:`, result.reason);
        }
      });

      // Track whether memberships/invitations failed so we can show the user
      const membershipsFailed = results[0].status === 'rejected';
      setMembershipsError(membershipsFailed);

      // Use data from React Query hooks (userOrganizations and userTeams)
      const orgsCount = userOrganizations.length;
      const teamsCount = userTeams.length;

      // Debug logging to understand data structure
      console.log('🚀 Dashboard data loaded:', {
        orgs: userOrganizations.length,
        orgsCount: orgsCount,
        orgsData: userOrganizations,
        teams: userTeams.length,
        teamsCount: teamsCount,
        teamsData: userTeams,
        memberships: userMembershipsResult?.memberships?.length || 0,
        invitations: Array.isArray(userInvitations) ? userInvitations.length : typeof userInvitations,
        invitationsData: userInvitations,
        results: results.map((r, i) => ({
          index: i,
          status: r.status,
          error: r.status === 'rejected' ? r.reason?.message : null
        }))
      });

      // Calculate summary from actual data with proper total counts
      const invitationsArray = Array.isArray(userInvitations) ? userInvitations : [];
      const pendingInvitations = invitationsArray.filter((inv: any) => inv.status === 0 || inv.status === 'pending');
      const calculatedSummary = {
        organizationsCount: orgsCount,
        teamsCount: teamsCount,
        pendingInvitationsCount: pendingInvitations.length,
        membershipsCount: userMembershipsResult?.memberships?.length || 0
      };

      // Create dashboard data from calculated summary
      const finalDashboard = {
        summary: calculatedSummary
      };

      setDashboardData(finalDashboard);

      // Check if any critical API calls failed — membershipsError state covers the rest
      const failedCalls = results.filter(r => r.status === 'rejected').length;
      if (failedCalls > 0) {
        console.warn(`${failedCalls} API calls failed, but showing partial data`);
      }

      // Also refresh invitations - but use new user-centric endpoint
      if (!hasRefreshedInvitations.current) {
        try {
          // TODO: Replace with new user-centric invitation endpoint when available
          // For now, skip invitation refresh to avoid legacy endpoint calls
          console.log('Dashboard: Skipping invitation refresh to avoid legacy endpoint calls');
        } catch (error) {
          console.warn('Dashboard: Could not refresh invitations:', error);
        }
        hasRefreshedInvitations.current = true;
      }
    } catch (err: any) {
      console.error('Critical error loading dashboard data:', err);
      setError('Failed to load dashboard data. Please refresh the page.');

      // Set empty dashboard data
      setDashboardData({
        summary: {
          organizationsCount: 0,
          teamsCount: 0,
          pendingInvitationsCount: 0,
          membershipsCount: 0
        }
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRefreshAll = useCallback(() => {
    setMembershipsError(false);
    setError(null);
    refetchOrgs();
    refetchTeams();
    loadDashboardData();
  }, [refetchOrgs, refetchTeams]);

  // Load dashboard data when React Query data is ready
  useEffect(() => {
    // Wait for React Query to finish loading orgs and teams
    if (!orgsLoading && !teamsLoading && user) {
      loadDashboardData();
    }
  }, [user, orgsLoading, teamsLoading, userOrganizations, userTeams]);

  if (!user) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header Section */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h1"
          sx={{ fontSize: 32, fontWeight: 800, letterSpacing: '-0.025em', lineHeight: 1.15, marginBottom: 1 }}
        >
          Welcome back, {user.name || user.email.split('@')[0]}
        </Typography>
        <Typography sx={ledeSx}>
          Here's what's happening across your organizations and teams.
        </Typography>
      </Box>
      
      {/* Email Verification Alert */}
      {!user.isEmailVerified && (
        <Alert
          severity="warning"
          sx={{ mb: 4 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={handleResendVerification}
              disabled={resendingVerification}
            >
              {resendingVerification ? 'Sending...' : 'Resend'}
            </Button>
          }
        >
          <AlertTitle>Email Not Verified</AlertTitle>
          Please check your inbox for a verification link or click Resend to get a new link.
        </Alert>
      )}

      {/* Verification Message Alert */}
      {verificationMessage && (
        <Alert
          severity={verificationMessage.includes('Failed') ? 'error' : 'success'}
          sx={{ mb: 4 }}
          onClose={() => setVerificationMessage(null)}
        >
          {verificationMessage}
        </Alert>
      )}

      {/* Error Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 4 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Partial data warning — shown when one or more sections failed to load */}
      {!loading && (orgsError || teamsError || membershipsError) && (
        <Alert
          severity="warning"
          sx={{ mb: 4 }}
          action={
            <Button
              color="inherit"
              size="small"
              startIcon={<Refresh />}
              onClick={handleRefreshAll}
            >
              Retry
            </Button>
          }
        >
          <AlertTitle>Some information could not be loaded</AlertTitle>
          Your{' '}
          {[
            orgsError && 'organizations',
            teamsError && 'teams',
            membershipsError && 'membership details',
          ]
            .filter(Boolean)
            .join(', ')
            .replace(/,([^,]*)$/, ' and$1')}{' '}
          could not be loaded. The information shown below may be incomplete. Click{' '}
          <strong>Retry</strong> to try again, or reload the page.
        </Alert>
      )}

      {/* Subscription Expiration Warnings */}
      <SubscriptionExpirationWarnings
        subscriptions={allUserSubscriptions}
        onManageSubscription={async (subscription) => {
          // Navigate to subscription management for the organization
          // When switching organizations, clear team selection so the workspace selector shows the organization
          if (subscription.organizationId) {
            // Clear team selection first if it doesn't belong to target organization
            if (currentTeamId) {
              const team = teams.find(t => t.id === currentTeamId);
              if (!team || team.organizationId !== subscription.organizationId) {
                await setCurrentTeamId(null, false);
              }
            }

            // Update organization context and navigate to subscription tab
            setCurrentOrganizationId(subscription.organizationId, false);
            navigate(`/organizations/${subscription.organizationId}?tab=5`);
          }
        }}
      />

      <Grid container rowSpacing={3} columnSpacing={3}>
        {/* Summary Cards */}
        <Grid xs={12}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }}>
            <Tooltip
              title={orgsError ? 'Could not load organizations — click Retry in the banner above' : ''}
              disableHoverListener={!orgsError}
            >
              <span>
                <StatCard
                  label="Organizations"
                  value={loading ? '...' : orgsError ? '—' : (dashboardData?.summary.organizationsCount ?? userOrganizations.length)}
                  helper={orgsError ? 'Unable to load' : `Managing ${userOrganizations.length} workspace${userOrganizations.length !== 1 ? 's' : ''}`}
                  icon={<Business />}
                  color={orgsError ? 'error' : 'primary'}
                  loading={loading}
                />
              </span>
            </Tooltip>

            <Tooltip
              title={teamsError ? 'Could not load teams — click Retry in the banner above' : ''}
              disableHoverListener={!teamsError}
            >
              <span>
                <StatCard
                  label="Teams"
                  value={loading ? '...' : teamsError ? '—' : (dashboardData?.summary.teamsCount ?? userTeams.length)}
                  helper={teamsError ? 'Unable to load' : `Active across all organizations`}
                  icon={<Group />}
                  color={teamsError ? 'error' : 'secondary'}
                  loading={loading}
                />
              </span>
            </Tooltip>

            <Box
              component={Link}
              to="/invitations"
              sx={{
                textDecoration: 'none',
                '&:hover .stat-card': {
                  borderColor: 'warning.main',
                }
              }}
            >
              <StatCard
                label="Pending Invitations"
                value={loading ? '...' : pendingInvitationsCount}
                helper={pendingInvitationsCount > 0 ? 'Awaiting your response' : 'All caught up!'}
                icon={<NotificationsActive />}
                color="warning"
                loading={loading}
                sx={{ cursor: 'pointer' }}
              />
            </Box>

            <Tooltip
              title={membershipsError ? 'Could not load membership details — click Retry in the banner above' : ''}
              disableHoverListener={!membershipsError}
            >
              <span>
                <StatCard
                  label="Active Memberships"
                  value={loading ? '...' : membershipsError ? '—' : (dashboardData?.summary.membershipsCount ?? 0)}
                  helper={membershipsError ? 'Unable to load' : 'Total organization and team roles'}
                  icon={<Security />}
                  color={membershipsError ? 'error' : 'success'}
                  loading={loading}
                />
              </span>
            </Tooltip>
          </Box>
        </Grid>

        {/* Pending Invitations Section */}
        {pendingInvitationsCount > 0 && (
          <Grid xs={12}>
            <Card sx={{ mb: 3 }}>
              <CardHeader
                avatar={
                  <Badge badgeContent={pendingInvitationsCount} color="warning">
                    <NotificationsActive color="primary" />
                  </Badge>
                }
                title="Pending Invitations"
                subheader={`You have ${pendingInvitationsCount} invitation${pendingInvitationsCount !== 1 ? 's' : ''} waiting for your response`}
                action={
                  <Button 
                    component={Link} 
                    to="/invitations"
                    endIcon={<ArrowForward />}
                    size="small"
                  >
                    View All
                  </Button>
                }
              />
              <CardContent sx={{ pt: 0 }}>
                <InvitationList 
                  userOnly 
                  limit={3} 
                  onInvitationAction={(action) => {
                    if (action === 'accept') {
                      // Refresh the Dashboard data instead of context data
                      loadDashboardData();
                    }
                  }}
                />
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Main Content */}
        <Grid xs={12} lg={8}>
          {/* Organizations Section */}
          <Card
            sx={{
              mb: 3,
              borderRadius: 2,
              borderColor: 'divider',
              boxShadow: 'none',
              '&:hover': { transform: 'none', boxShadow: 'none' }
            }}
          >
            <CardHeader
              avatar={<Business color="primary" />}
              title="Your Organizations"
              subheader="Switch between workspaces and manage teams"
              action={
                <Button
                  component={Link}
                  to="/organizations/new"
                  startIcon={<AddIcon />}
                  variant="outlined"
                  size="small"
                >
                  New organization
                </Button>
              }
            />
            <Divider />
            <CardContent sx={{ pt: 1 }}>
              {loading ? (
                <Stack spacing={2}>
                  {[1, 2, 3].map((i) => (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Skeleton variant="circular" width={40} height={40} />
                      <Box sx={{ flexGrow: 1 }}>
                        <Skeleton width="60%" height={24} />
                        <Skeleton width="40%" height={20} />
                      </Box>
                    </Box>
                  ))}
                </Stack>
              ) : orgsError ? (
                <Alert
                  severity="warning"
                  action={
                    <Button color="inherit" size="small" startIcon={<Refresh />} onClick={() => refetchOrgs()}>
                      Retry
                    </Button>
                  }
                >
                  Could not load your organizations. Please try again.
                </Alert>
              ) : userOrganizations.length > 0 ? (
                <List>
                  {userOrganizations.slice(0, 5).map((org, index) => {
                    const orgTeams = userTeams.filter(team => team.organizationId === (org.id || org._id));

                    return (
                      <React.Fragment key={org.id || org._id}>
                        <ListItem
                          component={Link}
                          to={`/organizations/${org.id || org._id}`}
                          onClick={() => syncWithCurrentRoute(org.id || org._id)}
                          sx={{
                            textDecoration: 'none',
                            color: 'inherit',
                            borderRadius: 1,
                            '&:hover': {
                              bgcolor: 'action.hover',
                            }
                          }}
                        >
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                                  {org.name}
                                </Typography>
                                {org.userRole && (
                                  <Chip
                                    label={org.userRole}
                                    size="small"
                                    variant="outlined"
                                    sx={{ height: 20, fontSize: '0.7rem' }}
                                  />
                                )}
                              </Box>
                            }
                            secondary={
                              <Typography variant="body2" color="text.secondary">
                                {orgTeams.length} team{orgTeams.length !== 1 ? 's' : ''}
                              </Typography>
                            }
                          />
                          <ListItemSecondaryAction>
                            <Button
                              component={Link}
                              to={`/organizations/${org.id || org._id}`}
                              onClick={() => syncWithCurrentRoute(org.id || org._id)}
                              size="small"
                              endIcon={<ArrowForward />}
                            >
                              Open
                            </Button>
                          </ListItemSecondaryAction>
                        </ListItem>
                        {index < userOrganizations.slice(0, 5).length - 1 && <Divider component="li" />}
                      </React.Fragment>
                    );
                  })}
                </List>
              ) : (
                <EmptyState
                  icon={<Business sx={{ fontSize: 48 }} />}
                  title="No organizations yet"
                  description="Create your first organization to start collaborating with teams."
                  actionLabel="Create organization"
                  actionTo="/organizations/new"
                />
              )}
            </CardContent>
          </Card>

          {/* Teams Section */}
          <Card
            sx={{
              borderRadius: 2,
              borderColor: 'divider',
              boxShadow: 'none',
              '&:hover': { transform: 'none', boxShadow: 'none' }
            }}
          >
            <CardHeader
              avatar={<Group color="secondary" />}
              title="Your Teams"
              subheader="Jump back into the workspaces you use most"
            />
            <Divider />
            <CardContent sx={{ pt: 1 }}>
              {loading ? (
                <Stack spacing={2}>
                  {[1, 2, 3].map((i) => (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Skeleton variant="circular" width={40} height={40} />
                      <Box sx={{ flexGrow: 1 }}>
                        <Skeleton width="60%" height={24} />
                        <Skeleton width="40%" height={20} />
                      </Box>
                    </Box>
                  ))}
                </Stack>
              ) : teamsError ? (
                <Alert
                  severity="warning"
                  action={
                    <Button color="inherit" size="small" startIcon={<Refresh />} onClick={() => refetchTeams()}>
                      Retry
                    </Button>
                  }
                >
                  Could not load your teams. Please try again.
                </Alert>
              ) : userTeams.length > 0 ? (
                <List>
                  {userTeams.slice(0, 5).map((team, index) => {
                    const org = userOrganizations.find(o => (o.id || o._id) === team.organizationId);

                    return (
                      <React.Fragment key={team.id || team._id}>
                        <ListItem
                          component="div"
                          onClick={async (e) => {
                            e.preventDefault();
                            const teamId = team.id || team._id;
                            try {
                              setActivatingTeamId(teamId);
                              // Set the organization and team context
                              setCurrentOrganizationId(team.organizationId);
                              await setCurrentTeamId(teamId, true); // true = navigate to content page
                            } finally {
                              setActivatingTeamId(null);
                            }
                          }}
                          sx={{
                            cursor: 'pointer',
                            textDecoration: 'none',
                            color: 'inherit',
                            borderRadius: 1,
                            '&:hover': {
                              bgcolor: 'action.hover',
                            }
                          }}
                        >
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                                  {team.name}
                                </Typography>
                                {team.userRole && (
                                  <Chip
                                    label={team.userRole}
                                    size="small"
                                    variant="outlined"
                                    sx={{ height: 20, fontSize: '0.7rem' }}
                                  />
                                )}
                              </Box>
                            }
                            secondary={
                              <Typography variant="body2" color="text.secondary">
                                {org?.name || team.organizationName || 'Unknown Organization'}
                              </Typography>
                            }
                          />
                          <ListItemSecondaryAction>
                            <Button
                              onClick={async (e) => {
                                e.stopPropagation();
                                const teamId = team.id || team._id;
                                try {
                                  setActivatingTeamId(teamId);
                                  setCurrentOrganizationId(team.organizationId);
                                  await setCurrentTeamId(teamId, true);
                                } finally {
                                  setActivatingTeamId(null);
                                }
                              }}
                              size="small"
                              endIcon={activatingTeamId === (team.id || team._id) ? <CircularProgress size={16} /> : <ArrowForward />}
                              disabled={activatingTeamId === (team.id || team._id)}
                            >
                              Open
                            </Button>
                          </ListItemSecondaryAction>
                        </ListItem>
                        {index < userTeams.slice(0, 5).length - 1 && <Divider component="li" />}
                      </React.Fragment>
                    );
                  })}
                </List>
              ) : (
                <EmptyState
                  icon={<Group sx={{ fontSize: 48 }} />}
                  title="No teams yet"
                  description="Join a team or create one within an organization to start collaborating."
                />
              )}
            </CardContent>
          </Card>

          {/* Views Section */}
          <Box sx={{ mt: 3 }}>
            <ViewsDashboardCard />
          </Box>
        </Grid>

        {/* Sidebar */}
        <Grid xs={12} lg={4}>
          {/* Quick Actions */}
          <Card
            sx={{
              mb: 3,
              borderRadius: 2,
              borderColor: 'divider',
              boxShadow: 'none',
              '&:hover': { transform: 'none', boxShadow: 'none' }
            }}
          >
            <CardHeader
              avatar={<Star color="warning" />}
              title="Quick Actions"
              subheader="Common tasks you might want to do now"
            />
            <Divider />
            <CardContent sx={{ pt: 2 }}>
              <Stack spacing={1.5}>
                <Button
                  component={Link}
                  to="/organizations/new"
                  variant="outlined"
                  startIcon={<AddIcon />}
                  fullWidth
                  sx={{ justifyContent: 'flex-start' }}
                >
                  Create Organization
                </Button>
                <Button
                  component={Link}
                  to="/profile"
                  variant="outlined"
                  startIcon={<Settings />}
                  fullWidth
                  sx={{ justifyContent: 'flex-start' }}
                >
                  Update Profile
                </Button>
                <Button
                  onClick={handleRefreshAll}
                  variant="outlined"
                  startIcon={<Refresh />}
                  fullWidth
                  sx={{ justifyContent: 'flex-start' }}
                >
                  Refresh Data
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Dashboard;
